import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import WikiSidebar from "../components/wiki/WikiSidebar";
import WikiEditor from "../components/wiki/WikiEditor";
import WikiViewer from "../components/wiki/WikiViewer";
import WikiSearch from "../components/wiki/WikiSearch";
import { wikiApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
    Search,
    PanelLeftClose,
    PanelLeft,
    Plus,
    FileText,
} from "lucide-react";

export default function WikiLanding() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = Boolean(user?.is_admin);

    const [tree, setTree] = useState([]);
    const [currentPage, setCurrentPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [searchOpen, setSearchOpen] = useState(false);
    const [editing, setEditing] = useState(false);

    // Build tree structure
    const treeNodes = useMemo(() => {
        const byParent = {};
        const map = {};
        tree.forEach((p) => {
            map[p.id] = { ...p, children: [] };
            const pid = p.parent_id || "__root__";
            if (!byParent[pid]) byParent[pid] = [];
            byParent[pid].push(map[p.id]);
        });
        // nest children
        Object.keys(byParent).forEach((pid) => {
            if (pid !== "__root__" && map[pid]) {
                map[pid].children = byParent[pid] || [];
            }
        });
        return byParent["__root__"] || [];
    }, [tree]);

    const loadTree = useCallback(async () => {
        try {
            const t = await wikiApi.tree();
            setTree(t);
        } catch {
            /* ignore */
        }
    }, []);

    // Load tree on mount
    useEffect(() => {
        loadTree().finally(() => setLoading(false));
    }, [loadTree]);

    // Load page when slug changes
    useEffect(() => {
        if (slug) {
            wikiApi
                .getPage(slug)
                .then((p) => {
                    setCurrentPage(p);
                    setEditing(false);
                })
                .catch(() => setCurrentPage(null));
        } else {
            setCurrentPage(null);
            setEditing(false);
        }
    }, [slug]);

    // Cmd+K for search
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const handleSelectPage = (pageSlug) => {
        navigate(`/wiki/${pageSlug}`);
    };

    const handleCreatePage = async (parentId) => {
        const title = "New Page";
        const data = await wikiApi.createPage({
            title,
            parent_id: parentId || null,
        });
        await loadTree();
        navigate(`/wiki/${data.slug}`);
        setEditing(true);
    };

    const handleDeletePage = async (id) => {
        await wikiApi.deletePage(id);
        await loadTree();
        if (currentPage?.id === id) {
            navigate("/wiki");
        }
    };

    const handleRenamePage = async (id, newTitle) => {
        await wikiApi.updatePage(id, { title: newTitle });
        await loadTree();
        if (currentPage?.id === id) {
            setCurrentPage({ ...currentPage, title: newTitle });
        }
    };

    const handleSaveContent = async (body, plain_text) => {
        if (!currentPage) return;
        const res = await wikiApi.updateContent(currentPage.id, {
            body,
            plain_text,
        });
        setCurrentPage({ ...currentPage, body, plain_text, updated_at: res.updated_at });
        await loadTree();
    };

    // Get breadcrumb path
    const breadcrumbs = useMemo(() => {
        if (!currentPage) return [];
        const path = [];
        const map = {};
        tree.forEach((p) => (map[p.id] = p));
        let cur = currentPage;
        while (cur) {
            path.unshift(cur);
            cur = cur.parent_id ? map[cur.parent_id] : null;
        }
        return path;
    }, [currentPage, tree]);

    return (
        <div className="min-h-screen bg-white">
            <TopNav />
            <div className="flex" style={{ minHeight: "calc(100vh - 60px)" }}>
                {/* Sidebar */}
                {sidebarOpen && (
                    <aside className="w-64 lg:w-72 border-r-3 border-black bg-gray-50 flex-shrink-0 flex flex-col">
                        <div className="p-3 border-b-3 border-black flex items-center justify-between">
                            <h2 className="font-display text-sm tracking-wide">
                                KNOWLEDGE BASE
                            </h2>
                            <div className="flex items-center gap-1">
                                {isAdmin && (
                                    <button
                                        onClick={() => handleCreatePage(null)}
                                        className="p-1.5 bg-black text-white brut-border brut-press"
                                        title="New page"
                                    >
                                        <Plus size={14} strokeWidth={3} />
                                    </button>
                                )}
                                <button
                                    onClick={() => setSidebarOpen(false)}
                                    className="p-1.5 bg-white brut-border brut-press"
                                    title="Collapse sidebar"
                                >
                                    <PanelLeftClose size={14} strokeWidth={3} />
                                </button>
                            </div>
                        </div>
                        <WikiSidebar
                            tree={treeNodes}
                            selectedId={currentPage?.id}
                            onSelect={handleSelectPage}
                            onCreate={isAdmin ? handleCreatePage : undefined}
                            onDelete={isAdmin ? handleDeletePage : undefined}
                            onRename={isAdmin ? handleRenamePage : undefined}
                        />
                    </aside>
                )}

                {/* Main content */}
                <main className="flex-1 min-w-0">
                    {/* Toolbar */}
                    <div className="border-b-3 border-black px-6 py-3 flex items-center gap-3 flex-wrap">
                        {!sidebarOpen && (
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="p-1.5 bg-white brut-border brut-press"
                                title="Open sidebar"
                            >
                                <PanelLeft size={14} strokeWidth={3} />
                            </button>
                        )}
                        {!sidebarOpen && (
                            <button
                                onClick={() => handleCreatePage(null)}
                                className="font-display text-xs px-3 py-1.5 bg-white brut-border brut-press"
                            >
                                PAGES
                            </button>
                        )}

                        {/* Breadcrumbs */}
                        <div className="flex items-center gap-1 text-sm font-mono flex-1 min-w-0 overflow-hidden">
                            <button
                                onClick={() => navigate("/wiki")}
                                className="opacity-60 hover:opacity-100 shrink-0"
                            >
                                Wiki
                            </button>
                            {breadcrumbs.map((b, i) => (
                                <span key={b.id} className="flex items-center gap-1 shrink-0">
                                    <span className="opacity-40">/</span>
                                    {i < breadcrumbs.length - 1 ? (
                                        <button
                                            onClick={() => navigate(`/wiki/${b.slug}`)}
                                            className="opacity-60 hover:opacity-100 truncate max-w-[120px]"
                                        >
                                            {b.title}
                                        </button>
                                    ) : (
                                        <span className="font-bold truncate max-w-[200px]">
                                            {b.title}
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>

                        {/* Search + Edit */}
                        <button
                            onClick={() => setSearchOpen(true)}
                            className="font-display text-xs px-3 py-1.5 bg-white brut-border brut-press flex items-center gap-1.5"
                        >
                            <Search size={14} strokeWidth={3} />
                            <span className="hidden sm:inline">Search</span>
                            <kbd className="hidden sm:inline text-[10px] opacity-50 ml-1 border border-black px-1">
                                ⌘K
                            </kbd>
                        </button>
                        {isAdmin && currentPage && !editing && (
                            <button
                                onClick={() => setEditing(true)}
                                className="font-display text-xs px-3 py-1.5 bg-[#E8241A] text-white brut-border brut-press"
                            >
                                EDIT
                            </button>
                        )}
                        {isAdmin && currentPage && editing && (
                            <button
                                onClick={() => setEditing(false)}
                                className="font-display text-xs px-3 py-1.5 bg-black text-white brut-border brut-press"
                            >
                                DONE EDITING
                            </button>
                        )}
                    </div>

                    {/* Content area */}
                    <div className="max-w-4xl mx-auto px-6 py-8">
                        {loading ? (
                            <div className="font-display text-lg opacity-50">
                                LOADING…
                            </div>
                        ) : !currentPage && !slug ? (
                            /* Landing — no page selected */
                            <div className="text-center py-20">
                                <div className="bg-[#E8241A] brut-border brut-shadow p-6 inline-block mb-6">
                                    <FileText
                                        size={48}
                                        strokeWidth={2}
                                        className="text-white"
                                    />
                                </div>
                                <h1 className="font-display text-3xl sm:text-4xl mb-4">
                                    KNOWLEDGE BASE
                                </h1>
                                <p className="font-mono text-sm opacity-70 max-w-md mx-auto mb-8">
                                    Internal documentation, guides, and resources.
                                    Select a page from the sidebar or search to get
                                    started.
                                </p>
                                <button
                                    onClick={() => setSearchOpen(true)}
                                    className="font-display px-6 py-3 bg-black text-white brut-border brut-shadow brut-press"
                                >
                                    SEARCH PAGES (⌘K)
                                </button>
                            </div>
                        ) : !currentPage && slug ? (
                            <div className="brut-border bg-white p-8 text-center">
                                <p className="font-display text-xl mb-2">
                                    PAGE NOT FOUND
                                </p>
                                <p className="font-mono text-sm opacity-60">
                                    The page "{slug}" doesn't exist.
                                </p>
                                <button
                                    onClick={() => navigate("/wiki")}
                                    className="mt-4 font-display text-sm px-4 py-2 bg-black text-white brut-border brut-press"
                                >
                                    BACK TO WIKI
                                </button>
                            </div>
                        ) : currentPage && editing ? (
                            <WikiEditor
                                key={currentPage.id + "-edit"}
                                page={currentPage}
                                onSave={handleSaveContent}
                            />
                        ) : currentPage ? (
                            <WikiViewer
                                key={currentPage.id + "-view"}
                                page={currentPage}
                            />
                        ) : null}
                    </div>
                </main>
            </div>

            {/* Search dialog */}
            {searchOpen && (
                <WikiSearch
                    onClose={() => setSearchOpen(false)}
                    onSelect={(s) => {
                        setSearchOpen(false);
                        navigate(`/wiki/${s}`);
                    }}
                />
            )}
        </div>
    );
}
